import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, FileDown, Loader2 } from "lucide-react";
import { BULK_ACTION_TYPE_LABEL, type BulkAction } from "@somasi/shared";

import { api } from "@/integrations/aws/resources";
import { getBulkActionFileUrl, getInvoiceRun } from "@/integrations/aws/api";
import { PageHeader } from "@/components/app-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BulkActionStatusBadge, StartedBy, formatStamp } from "@/lib/bulk-actions";
import { money } from "@/lib/billing";
import { notify } from "@/lib/notify";

export const Route = createFileRoute("/_authenticated/bulk-actions/$actionId")({
  head: () => ({ meta: [{ title: "Bulk Action — Somasi Ops" }] }),
  component: BulkActionPage,
});

const going = (status: BulkAction["status"] | undefined) =>
  status === "queued" || status === "running";

const OUTCOME_TONE = {
  pending: "bg-muted text-muted-foreground",
  done: "bg-success text-success-foreground",
  failed: "bg-destructive text-destructive-foreground",
} as const;

/** One bulk action: what it was, where it has got to, its files, and what it did. */
function BulkActionPage() {
  const { actionId } = Route.useParams();
  const [preparing, setPreparing] = useState<number | null>(null);

  const { data: action, isLoading } = useQuery({
    queryKey: ["bulk_actions", "one", actionId],
    queryFn: async () => {
      const { data, error } = await api.bulkActions.get(actionId);
      if (error) throw error;
      return data as BulkAction;
    },
    refetchInterval: (q) => (going(q.state.data?.status) ? 5000 : false),
  });

  // The invoices themselves are on the run the action names.
  const { data: run } = useQuery({
    enabled: action?.type === "invoice_generation",
    queryKey: ["invoice_run", action?.source_id],
    queryFn: () => getInvoiceRun(action!.source_id),
    refetchInterval: going(action?.status) ? 5000 : false,
  });

  const download = async (index: number) => {
    setPreparing(index);
    try {
      window.location.assign(await getBulkActionFileUrl(actionId, index));
    } catch (error) {
      notify.error(error);
    } finally {
      setPreparing(null);
    }
  };

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </p>
    );
  }
  if (!action) return <p className="text-sm text-muted-foreground">No such bulk action.</p>;

  return (
    <div className="space-y-5 pb-8">
      <PageHeader
        title={action.title}
        description={BULK_ACTION_TYPE_LABEL[action.type] ?? action.type}
        breadcrumbs={[{ label: "Bulk Actions", to: "/bulk-actions" }, { label: action.title }]}
        actions={
          <Button variant="outline" asChild>
            <Link to="/bulk-actions">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <BulkActionStatusBadge status={action.status} />
              </dd>
              <dt className="text-muted-foreground">Result</dt>
              <dd>{action.summary ?? "—"}</dd>
              <dt className="text-muted-foreground">Started by</dt>
              <dd>
                <StartedBy action={action} />
              </dd>
              <dt className="text-muted-foreground">Started</dt>
              <dd>{formatStamp(action.created_at)}</dd>
              {run && (
                <>
                  <dt className="text-muted-foreground">Created as</dt>
                  <dd>{run.issue === "draft" ? "Drafts" : "Issued invoices"}</dd>
                  <dt className="text-muted-foreground">Invoice date</dt>
                  <dd>{run.invoice_date}</dd>
                  {run.period_label && (
                    <>
                      <dt className="text-muted-foreground">Period</dt>
                      <dd>{run.period_label}</dd>
                    </>
                  )}
                </>
              )}
            </dl>
            {action.error && (
              <p className="mt-4 flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {action.error}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Files</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {action.files.length === 0 ? (
              <p className="text-muted-foreground">
                {going(action.status)
                  ? "The files appear here once the action finishes."
                  : "This action left no files."}
              </p>
            ) : (
              action.files.map((f, index) => (
                <div key={f.key} className="flex items-center justify-between gap-3">
                  <span className="truncate">{f.name}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={preparing === index}
                    onClick={() => void download(index)}
                  >
                    {preparing === index ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileDown className="mr-2 h-4 w-4" />
                    )}
                    Download
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {run && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {run.outcomes.map((o) => (
                  <TableRow key={o.company_id}>
                    <TableCell>{o.company_name}</TableCell>
                    <TableCell>
                      <Badge className={OUTCOME_TONE[o.state]}>
                        {o.state === "pending" ? "Pending" : o.state === "done" ? "Done" : "Failed"}
                      </Badge>
                      {o.message && (
                        <span className="ml-2 text-xs text-destructive">{o.message}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {o.invoice_id ? (
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: o.invoice_id }}
                          className="text-primary hover:underline"
                        >
                          {o.invoice_number ?? "Open"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {o.total !== undefined ? money(Number(o.total)) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
